import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { RichText } from 'prismic-reactjs';
import styled from 'styled-components';
import PrismicHTMLSerializer from '@components/shared/PrismicHTMLSerializer';
import PrismicLinkSerializer from '@components/shared/PrismicLinkSerializer';
import useDesktop from '@components/shared/useDesktop';
import ArticleNewsletterForm from '@components/vitals//ArticleNewsletterForm';
import ArticleProductCards from '@components/vitals/ArticleProductCards';
import ArticleShoppingBannerItem from '@components/vitals/ArticleShoppingBannerItem';
import ArticleShoppingModule from '@components/vitals/ArticleShoppingModule';
import { BREAKPOINT } from '@constants';
import { ArticleSliceType } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { getYouTubeEmbedUrl } from '@utils/helpers';
import { HeaderItem } from './utils/helpers';

export interface Props {
  articleItem: any;
  index: number;
  headersList: HeaderItem[];
}

export default function MainArticleContentItem({ articleItem, index, headersList }: Props) {
  const { country } = useCountry();
  const isMobile = !useDesktop(BREAKPOINT.laptop);

  const customPrismicHTMLSerializer = () => {
    return PrismicHTMLSerializer({ headersList });
  };

  switch (articleItem.slice_type) {
    case ArticleSliceType.RichText:
      return (
        <div className="rich-text" key={index}>
          <RichText
            render={articleItem.primary.rich_text.richText}
            serializeHyperlink={PrismicLinkSerializer}
            htmlSerializer={customPrismicHTMLSerializer()}
          ></RichText>
        </div>
      );
    case ArticleSliceType.Quote:
      return (
        <div key={index}>
          <QuoteContainer>
            <StyledP>“</StyledP>
            <StyledQuote>{articleItem.primary.quote.text}</StyledQuote>
          </QuoteContainer>
        </div>
      );
    case ArticleSliceType.SingleImage:
      return (
        <ImageWrap>
          <GatsbyImage alt={articleItem.primary.si_image.alt || ''} key={index} image={articleItem.primary.si_image.gatsbyImageData} loading="lazy" />
        </ImageWrap>
      );
    case ArticleSliceType.HorizontalRule:
      return (
        <ImageAndLineContainer key={index}>
          <ArticleShoppingModule id={'random-tampons'} />
        </ImageAndLineContainer>
      );
    case ArticleSliceType.ShoppingModule:
      return <ArticleShoppingModule id={articleItem.primary.banner_type} key={index} />;
    case ArticleSliceType.ShoppingBanner: {
      return <ArticleShoppingBannerItem key={index} country={country} details={articleItem.primary} />;
    }
    case ArticleSliceType.ProductCards: {
      // render ArticleProductCards on desktop in ArticlePageContent
      // because they need to be children of the main Article parent in the DOM tree
      return isMobile ? (
        <ContainerArticleProductCardsMobile>
          <ArticleProductCards articleItem={articleItem} isMobile={isMobile} />
        </ContainerArticleProductCardsMobile>
      ) : null;
    }
    case ArticleSliceType.VideoEmbed: {
      const videoEmbedUrl = getYouTubeEmbedUrl(articleItem.primary.video_url);
      if (!videoEmbedUrl) return null;
      return (
        <VideoEmbedContainer>
          <iframe src={`${videoEmbedUrl}`} title="YouTube video player" />
        </VideoEmbedContainer>
      );
    }
    case ArticleSliceType.Newsletter: {
      return <ArticleNewsletterForm country={country} className="newsletter" />;
    }
    default:
      return null;
  }
}

const ImageWrap = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  & img {
    object-fit: contain !important;
    max-height: 460px;
    margin-left: auto;
    max-width: 460px;
    display: flex;
    margin: auto;
    margin: 30px auto;
    @media only screen and (max-width: 500px) {
      max-width: 280px;
    }
  }
`;
const ImageAndLineContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledQuote = styled.p`
  font-family: Domaine;
  font-size: 42px !important;
  line-height: 1.24 !important;
  text-align: center;
  @media only screen and (max-width: 500px) {
    font-size: 36px !important;
  }
`;

const StyledP = styled.p`
  margin: 0;
  text-align: center;
  font-family: Domaine;
  font-size: 42px !important;
  line-height: 0.5 !important;
  @media only screen and (max-width: 500px) {
    font-size: 36px !important;
  }
`;

const QuoteContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 22px;
`;

const VideoEmbedContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding-top: 56.25%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContainerArticleProductCardsMobile = styled.div`
  margin-top: 56px;
  margin-bottom: 56px;
`;
